import React from 'react';
import PropTypes from 'prop-types';

// Shared hooks
import { useAuth } from '../../hooks/auth/useAuth';

/**
 * Inline Permission Checking Component
 *
 * Conditionally renders children based on RBAC permissions.
 * Useful for showing/hiding UI elements based on user permissions.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render if permission check passes
 * @param {string[]} [props.permissions] - Array of permissions (any one is sufficient)
 * @param {string[]} [props.allPermissions] - Array of permissions (all required)
 * @param {string[]} [props.roles] - Array of role names (any one is sufficient)
 * @param {number} [props.minimumRoleLevel] - Minimum role level required
 * @param {string} [props.organizationType] - Required organization type
 * @param {boolean} [props.requireOrganization] - Whether organization context is required
 * @param {React.ReactNode} [props.fallback] - Content to render if permission check fails
 * @param {boolean} [props.hideOnFail] - If true, renders nothing when check fails (overrides fallback)
 * @param {boolean} [props.invert] - If true, inverts the permission check (renders when permissions are NOT met)
 */
const RequirePermission = ({
  children,
  permissions = [],
  allPermissions = [],
  roles = [],
  minimumRoleLevel = null,
  organizationType = null,
  requireOrganization = false,
  fallback = null,
  hideOnFail = false,
  invert = false,
}) => {
  const {
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasMinimumRoleLevel,
    currentOrganization,
    currentOrgType,
  } = useAuth();

  // Not authenticated - no permissions
  if (!isAuthenticated) {
    return invert ? children : hideOnFail ? null : fallback;
  }

  // Check organization requirements
  if (requireOrganization && !currentOrganization) {
    return invert ? children : hideOnFail ? null : fallback;
  }

  // Check organization type requirements
  if (organizationType && currentOrgType !== organizationType) {
    return invert ? children : hideOnFail ? null : fallback;
  }

  // Check minimum role level requirement
  if (minimumRoleLevel !== null && !hasMinimumRoleLevel(minimumRoleLevel)) {
    return invert ? children : hideOnFail ? null : fallback;
  }

  // Check role requirements (any one role is sufficient)
  if (roles.length > 0) {
    const hasRequiredRole = roles.some((roleName) => hasRole(roleName));
    if (!hasRequiredRole) {
      return invert ? children : hideOnFail ? null : fallback;
    }
  }

  // Check permission requirements (any one permission is sufficient)
  if (permissions.length > 0 && !hasAnyPermission(permissions)) {
    return invert ? children : hideOnFail ? null : fallback;
  }

  // Check all permissions requirement (all permissions must be present)
  if (allPermissions.length > 0 && !hasAllPermissions(allPermissions)) {
    return invert ? children : hideOnFail ? null : fallback;
  }

  // All checks passed
  return invert ? (hideOnFail ? null : fallback) : children;
};

RequirePermission.propTypes = {
  children: PropTypes.node.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  allPermissions: PropTypes.arrayOf(PropTypes.string),
  roles: PropTypes.arrayOf(PropTypes.string),
  minimumRoleLevel: PropTypes.number,
  organizationType: PropTypes.oneOf(['fleetcam', 'client']),
  requireOrganization: PropTypes.bool,
  fallback: PropTypes.node,
  hideOnFail: PropTypes.bool,
  invert: PropTypes.bool,
};

export default RequirePermission;
